import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Target, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Pagination } from "@/components/pagination";
import { DataTable } from "@/components/data-table";
import { listPixelsPaged, createPixel, deletePixel, updatePixel } from "@/api/pixels";
import { apiErrorMessage } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import {
    PROVIDER_SPECS,
    providerSpec,
    type CreatePixelRequest,
    type PixelDto,
    type PixelProvider,
} from "@/types/pixel";

const PAGE_SIZE = 50;

export function PixelsListPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [pageIndex, setPageIndex] = React.useState(0);
    const [searchInput, setSearchInput] = React.useState("");
    const [search, setSearch] = React.useState("");
    const [providerFilter, setProviderFilter] = React.useState<string>("all");
    React.useEffect(() => {
        const t = setTimeout(() => setSearch(searchInput.trim()), 250);
        return () => clearTimeout(t);
    }, [searchInput]);
    React.useEffect(() => setPageIndex(0), [search, providerFilter]);

    const pixelsQuery = useQuery({
        queryKey: ["pixels", pageIndex, search, providerFilter],
        queryFn: () =>
            listPixelsPaged({
                page: pageIndex,
                size: PAGE_SIZE,
                sort: "updatedAt,desc",
                q: search || undefined,
                provider: providerFilter,
            }),
        placeholderData: (prev) => prev,
    });

    const rows = pixelsQuery.data?.content ?? [];

    const [createOpen, setCreateOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<PixelDto | null>(null);

    const deleteMut = useMutation({
        mutationFn: (id: number) => deletePixel(id),
        onSuccess: () => {
            toast.success("Pixel deleted");
            queryClient.invalidateQueries({ queryKey: ["pixels"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
    });

    const toggleEnabledMut = useMutation({
        mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
            updatePixel(id, { enabled }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["pixels"] });
        },
        onError: (e) => toast.error(apiErrorMessage(e)),
    });

    return (
        <>
            <PageHeader
                title="Pixels"
                description="Tracking and conversion pixels you embed in landing pages."
                actions={
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-pixel-button">
                                <Plus className="h-4 w-4" />
                                New pixel
                            </Button>
                        </DialogTrigger>
                        <PixelEditorDialog
                            mode="create"
                            onClose={() => setCreateOpen(false)}
                            onSaved={() => {
                                setCreateOpen(false);
                                queryClient.invalidateQueries({ queryKey: ["pixels"] });
                            }}
                        />
                    </Dialog>
                }
            />
            <div className="p-6 space-y-4">
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                            <Search className="h-4 w-4" /> Filters
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <div className="space-y-1.5">
                                <Label>Search</Label>
                                <Input
                                    data-testid="pixels-search"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="By name…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Provider</Label>
                                <Select value={providerFilter} onValueChange={setProviderFilter}>
                                    <SelectTrigger data-testid="pixels-provider-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All providers</SelectItem>
                                        {PROVIDER_SPECS.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {pixelsQuery.isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        icon={<Target className="h-8 w-8" />}
                        title="No pixels yet"
                        description="Create a pixel and attach it to a campaign or domain to start tracking conversions."
                    />
                ) : (
                    <>
                        <DataTable<PixelDto>
                            dataTestid="pixels-table"
                            columns={[
                                {
                                    key: "name",
                                    header: "Name",
                                    cell: (p) => (
                                        <button
                                            className="text-left font-medium hover:underline"
                                            onClick={() => setEditing(p)}
                                            data-testid={`pixel-row-${p.id}`}
                                        >
                                            {p.name}
                                        </button>
                                    ),
                                },
                                {
                                    key: "provider",
                                    header: "Provider",
                                    cell: (p) => (
                                        <Badge variant="muted">
                                            {providerSpec(p.provider)?.label ?? p.provider}
                                        </Badge>
                                    ),
                                },
                                {
                                    key: "id",
                                    header: "ID",
                                    cell: (p) => (
                                        <span className="font-mono text-xs text-muted-foreground">
                                            {p.externalId ?? (p.provider === "raw_html" ? "—" : "(missing)")}
                                        </span>
                                    ),
                                },
                                {
                                    key: "attachments",
                                    header: "Attached to",
                                    cell: (p) => (
                                        <span className="text-xs text-muted-foreground">
                                            {p.campaignAttachments} campaign{p.campaignAttachments === 1 ? "" : "s"} · {p.domainAttachments} domain
                                            {p.domainAttachments === 1 ? "" : "s"}
                                        </span>
                                    ),
                                },
                                {
                                    key: "enabled",
                                    header: "Enabled",
                                    cell: (p) => (
                                        <Switch
                                            checked={p.enabled}
                                            data-testid={`pixel-enabled-${p.id}`}
                                            onCheckedChange={(checked) =>
                                                toggleEnabledMut.mutate({ id: p.id, enabled: checked })
                                            }
                                        />
                                    ),
                                },
                                {
                                    key: "updated",
                                    header: "Updated",
                                    cell: (p) => (
                                        <span className="text-xs text-muted-foreground">
                                            {formatRelativeTime(p.updatedAt)}
                                        </span>
                                    ),
                                },
                                {
                                    key: "actions",
                                    header: "",
                                    cell: (p) => (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            aria-label={`Delete ${p.name}`}
                                            title="Delete"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const camp = p.campaignAttachments ?? 0;
                                                const dom = p.domainAttachments ?? 0;
                                                const total = camp + dom;
                                                confirm.show({
                                                    title: `Delete "${p.name}"?`,
                                                    description: total > 0
                                                        ? `This pixel is attached to ${camp} campaign${camp === 1 ? "" : "s"} and ${dom} domain${dom === 1 ? "" : "s"}. Removing it stops firing on all of them — past fire history is preserved.`
                                                        : "Removes the pixel record. No active attachments to detach.",
                                                    destructive: true,
                                                    confirmLabel: "Delete pixel",
                                                    onConfirm: async () => {
                                                        await new Promise<void>((resolve, reject) => {
                                                            deleteMut.mutate(p.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                                        });
                                                    },
                                                });
                                            }}
                                            data-testid={`pixel-delete-${p.id}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    ),
                                },
                            ]}
                            rows={rows}
                            rowKey={(p) => p.id}
                        />
                        {pixelsQuery.data && (
                            <Pagination
                                page={pixelsQuery.data}
                                pageSize={PAGE_SIZE}
                                currentPage={pageIndex}
                                onPage={setPageIndex}
                            />
                        )}
                    </>
                )}
            </div>

            <Dialog open={editing != null} onOpenChange={(open) => !open && setEditing(null)}>
                {editing && (
                    <PixelEditorDialog
                        mode="edit"
                        pixel={editing}
                        onClose={() => setEditing(null)}
                        onSaved={() => {
                            setEditing(null);
                            queryClient.invalidateQueries({ queryKey: ["pixels"] });
                        }}
                    />
                )}
            </Dialog>
            {confirm.dialog}
        </>
    );
}

interface EditorProps {
    mode: "create" | "edit";
    pixel?: PixelDto;
    onClose: () => void;
    onSaved: () => void;
}

function PixelEditorDialog({ mode, pixel, onClose, onSaved }: EditorProps): React.ReactNode {
    const initial: PixelProvider = pixel?.provider ?? "facebook";
    const [provider, setProvider] = React.useState<PixelProvider>(initial);
    const [name, setName] = React.useState(pixel?.name ?? "");
    const [externalId, setExternalId] = React.useState(pixel?.externalId ?? "");
    const [leadEvent, setLeadEvent] = React.useState(pixel?.leadEvent ?? "");
    const [rawHead, setRawHead] = React.useState(pixel?.rawHeadHtml ?? "");
    const [rawBody, setRawBody] = React.useState(pixel?.rawBodyHtml ?? "");
    const [config, setConfig] = React.useState<Record<string, string>>(() => {
        const obj: Record<string, string> = {};
        if (pixel?.config && typeof pixel.config === "object") {
            for (const [k, v] of Object.entries(pixel.config)) {
                if (typeof v === "string") obj[k] = v;
            }
        }
        return obj;
    });
    const [enabled, setEnabled] = React.useState(pixel?.enabled ?? true);
    const [error, setError] = React.useState<string | null>(null);
    const spec = providerSpec(provider);

    const saveMut = useMutation({
        mutationFn: async () => {
            const body: CreatePixelRequest = {
                provider,
                name: name.trim(),
                enabled,
                leadEvent: leadEvent.trim() || undefined,
                config: Object.fromEntries(
                    Object.entries(config).filter(([, v]) => v && v.trim().length > 0),
                ),
            };
            if (provider === "raw_html") {
                body.rawHeadHtml = rawHead;
                body.rawBodyHtml = rawBody;
            } else {
                body.externalId = externalId.trim();
            }
            if (mode === "create") {
                return await createPixel(body);
            }
            return await updatePixel(pixel!.id, body);
        },
        onSuccess: () => {
            toast.success(mode === "create" ? "Pixel created" : "Pixel updated");
            onSaved();
        },
        onError: (e) => setError(apiErrorMessage(e)),
    });

    return (
        <DialogContent className="max-w-2xl">
            <DialogHeader>
                <DialogTitle>{mode === "create" ? "New pixel" : `Edit pixel · ${pixel?.name}`}</DialogTitle>
                <DialogDescription>
                    {spec?.helpText ??
                        "Tracking pixel — embedded into landing pages via campaigns or domain attachment."}
                </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
                {mode === "create" && (
                    <div className="space-y-1.5">
                        <Label>Provider</Label>
                        <Select value={provider} onValueChange={(v) => setProvider(v as PixelProvider)}>
                            <SelectTrigger data-testid="pixel-provider-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PROVIDER_SPECS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input
                        data-testid="pixel-name-input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Facebook · Acquisition Q2"
                    />
                </div>

                {spec?.externalIdLabel && (
                    <div className="space-y-1.5">
                        <Label>{spec.externalIdLabel}</Label>
                        <Input
                            data-testid="pixel-external-id-input"
                            value={externalId}
                            onChange={(e) => setExternalId(e.target.value)}
                            placeholder={spec.externalIdPlaceholder}
                        />
                    </div>
                )}

                {spec?.configFields?.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                        <Label>{f.label}</Label>
                        <Input
                            value={config[f.key] ?? ""}
                            onChange={(e) => setConfig((c) => ({ ...c, [f.key]: e.target.value }))}
                            placeholder={f.placeholder}
                        />
                        {f.helpText && <p className="text-xs text-muted-foreground">{f.helpText}</p>}
                    </div>
                ))}

                {provider === "raw_html" ? (
                    <>
                        <div className="space-y-1.5">
                            <Label>Head HTML</Label>
                            <Textarea
                                rows={6}
                                value={rawHead}
                                onChange={(e) => setRawHead(e.target.value)}
                                placeholder="<script>…</script>"
                                className="font-mono text-xs"
                                data-testid="pixel-raw-head-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label>Body HTML (optional)</Label>
                            <Textarea
                                rows={3}
                                value={rawBody}
                                onChange={(e) => setRawBody(e.target.value)}
                                placeholder="<noscript><img …/></noscript>"
                                className="font-mono text-xs"
                            />
                        </div>
                    </>
                ) : (
                    spec?.defaultLeadEvent != null && (
                        <div className="space-y-1.5">
                            <Label>Lead event (optional)</Label>
                            <Input
                                value={leadEvent}
                                onChange={(e) => setLeadEvent(e.target.value)}
                                placeholder={spec.defaultLeadEvent}
                            />
                            <p className="text-xs text-muted-foreground">
                                Fired after a successful form submit. Leave blank to use the default ({spec.defaultLeadEvent}).
                            </p>
                        </div>
                    )
                )}

                <div className="flex items-center gap-3">
                    <Switch checked={enabled} onCheckedChange={setEnabled} />
                    <Label className="text-sm">Enabled</Label>
                </div>

                {error && (
                    <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="pixel-error">
                        {error}
                    </p>
                )}
            </div>

            <DialogFooter>
                <Button variant="ghost" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    onClick={() => {
                        setError(null);
                        if (!name.trim()) return setError("Name is required");
                        if (spec?.externalIdLabel && !externalId.trim()) {
                            return setError(`${spec.externalIdLabel} is required`);
                        }
                        if (provider === "raw_html" && !rawHead.trim() && !rawBody.trim()) {
                            return setError("At least one of Head HTML or Body HTML is required");
                        }
                        saveMut.mutate();
                    }}
                    disabled={saveMut.isPending}
                    data-testid="pixel-save-button"
                >
                    {saveMut.isPending ? "Saving…" : mode === "create" ? "Create pixel" : "Save changes"}
                </Button>
            </DialogFooter>
        </DialogContent>
    );
}
